import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { errorToast } from "@idwal/idwal-react-components";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import {
    selectInspectionImages,
    selectResolvedAssets,
    selectSelectedInspection,
} from "../../store/selectors/answerSelectors";
import { cacheResolvedAssets } from "../../store/slices/answerSlice";
import { setViewImages } from "../../store/slices/inspectionSlice";
import { getAssetsByIds } from "../../services/inspectionService";
import { loadAssetImages } from "../../utils/imageHelper";
import {
    findQuestionImages,
    getUncachedAssetIds,
    keyAssetsById,
    orderCachedAssets,
} from "../../utils/questionImageHelper";
import { ChecklistReviewRow } from "../../types/reviewAnswer";

type Props = {
    row: ChecklistReviewRow;
};

/**
 * Images column cell for the Checklist Review / Surveyor Submission tables.
 * Shows a `pi pi-images` icon with the photo count for the question. When there
 * are photos the icon is actionable and opens the full-screen gallery; when
 * there are none it renders greyed out with a zero. Asset URLs are resolved
 * lazily on click (a question's photos are only a handful of assets).
 */
export const QuestionImageCell = ({ row }: Props) => {
    const { t } = useTranslation("locale");
    const dispatch = useAppDispatch();
    const inspectionImages = useAppSelector(selectInspectionImages);
    const selectedInspection = useAppSelector(selectSelectedInspection);
    const resolvedAssets = useAppSelector(selectResolvedAssets);
    const [loading, setLoading] = useState(false);

    const entry = findQuestionImages(inspectionImages, row.questionId);
    const count = entry?.count ?? 0;

    const onView = useCallback(async () => {
        if (!entry?.images.length || loading) return;
        setLoading(true);
        try {
            const assetIds = entry.images.map((image) => image.assetId);

            // Only resolve the presigned URLs we haven't already this session.
            const uncachedIds = getUncachedAssetIds(assetIds, resolvedAssets);
            let cache = resolvedAssets;
            if (uncachedIds.length) {
                const fetched = await getAssetsByIds(uncachedIds, selectedInspection?.id);
                if (fetched.length) {
                    dispatch(cacheResolvedAssets(fetched));
                    cache = { ...cache, ...keyAssetsById(fetched) };
                }
            }

            // Blobs aren't cached (memory) — download and mint objectURLs per
            // open; QuestionImagesGalleria revokes them on close.
            const assets = orderCachedAssets(assetIds, cache);
            const loaded = await loadAssetImages(assets);
            if (loaded.length) {
                dispatch(setViewImages(loaded));
            } else {
                // The resolve/download helpers swallow their own failures and
                // return empty, so this is the point where the user would
                // otherwise get no feedback and the gallery silently wouldn't open.
                errorToast(t("answers.images.loadError"));
            }
        } catch (e) {
            console.warn(e);
            errorToast(t("answers.images.loadError"));
        } finally {
            setLoading(false);
        }
    }, [dispatch, entry, loading, resolvedAssets, selectedInspection, t]);

    if (count === 0) {
        return (
            <span className="review-images-cell review-images-cell--empty" aria-label={t("answers.images.none")}>
                <i className="pi pi-images" aria-hidden="true" />
                <span className="review-images-count">0</span>
            </span>
        );
    }

    return (
        <button
            type="button"
            className="review-images-cell review-images-cell--action"
            onClick={onView}
            disabled={loading}
            title={t("answers.images.view", { count })}
        >
            <i className={loading ? "pi pi-spin pi-spinner" : "pi pi-images"} aria-hidden="true" />
            <span className="review-images-count">{count}</span>
        </button>
    );
};
